import { Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ImportCheckedRow, ImportColumnHead } from '@/api/imports';
import { cellKey, issuesByCell, rowLevelIssues, rowSeverity } from '@/domain/imports';
import { CnyHint } from '@/components/common/AmountWithCNY';

const { Text } = Typography;

/** 与后端 imports.columns.ts 的 key 对齐。只用来在表格里认出金额与币种那两列。 */
const AMOUNT_KEY = 'amount';
const CURRENCY_KEY = 'accountCurrency';

/** 出错的格子用红字 + 下面一行小字说清怎么改。「格式不正确」这种话不算说清。 */
function Cell({ value, issue }: { value: string | null; issue?: { message: string; severity: 'error' | 'notice' } }) {
  const color = issue?.severity === 'error' ? '#ff4d4f' : issue ? '#d48806' : undefined;
  return (
    <Space direction="vertical" size={0}>
      <Text style={{ color, whiteSpace: 'nowrap' }}>{value ?? '—'}</Text>
      {issue && (
        <Text style={{ color, fontSize: 12, whiteSpace: 'normal', maxWidth: 260, display: 'inline-block' }}>
          {issue.message}
        </Text>
      )}
    </Space>
  );
}

export default function ImportRowsTable({
  columns,
  rows,
}: {
  columns: ImportColumnHead[];
  rows: ImportCheckedRow[];
}) {
  const cellIssues = issuesByCell(rows);

  return (
    <Table<ImportCheckedRow>
      rowKey="rowNumber"
      dataSource={rows}
      pagination={false}
      size="small"
      // 与另外四个列表页一致：列宽超出内容区时横向滚动
      scroll={{ x: 'max-content', y: 480 }}
      columns={[
        {
          title: 'Excel 行号',
          dataIndex: 'rowNumber',
          width: 92,
          fixed: 'left',
          render: (n: number, row) => (
            <Space direction="vertical" size={0}>
              <Text strong>{n}</Text>
              {rowSeverity(row) === 'error' && <Tag color="red">有问题</Tag>}
              {rowLevelIssues(row).map((i, idx) => (
                // 🔴 key 不能只用 i.code：同一行完全可能出现两条同 code 的行级 issue
                // （比如两次都判成 SCHEMA_HIDDEN_REQUIRED），只用 code 当 key 会重复
                <Tooltip key={`${i.code}-${idx}`} title={i.message}>
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {i.message}
                  </Text>
                </Tooltip>
              ))}
            </Space>
          ),
        },
        // 🔴 按 key 从 row.cells 取值，不按列序 —— 列序变了也不会错位。
        // 显示的一律是**用户填的原始值**：出错的行没有 draft，而那正是最需要看到原文的行
        ...columns.map((c) => ({
          title: c.title,
          key: c.key,
          width: c.key === AMOUNT_KEY ? 180 : 160,
          render: (_: unknown, row: ImportCheckedRow) => {
            const value = row.cells[c.key] ?? null;
            const issue = cellIssues.get(cellKey(row.rowNumber, c.title));
            if (c.key !== AMOUNT_KEY) return <Cell value={value} issue={issue} />;
            // 🔴 金额那一格补一行人民币参考值。批量场景下「多打一个零」的代价是单笔的 N 倍，
            // 多一个熟悉的币种就多一次发现数量级错误的机会（PRODUCT.md §7）。
            // 拿不到汇率时 CnyHint 自己整行不显示，不要在这里另写一份判断
            const currency = row.cells[CURRENCY_KEY] ?? null;
            return (
              <Space direction="vertical" size={0}>
                <Cell value={value} issue={issue} />
                {value && currency && !issue && <CnyHint amount={value} currency={currency} />}
              </Space>
            );
          },
        })),
      ]}
    />
  );
}
